/**
 * Young leaning willow (`hush.sapling.v1`) — a ground piece for masking a roof corner of the hideout.
 *
 * Frame 150×240, support at the bottom-center (75, 240). A slender trunk leans from the ground (`lean` −1..1,
 * positive = right), a few branches arch out of its upper third, and long weeping leaf curtains hang from a soft
 * dome in three greens (dark back curtains, mid curtains, pale highlight streaks). `height` (0.7..1) scales the
 * tree inside the frame with the trunk base fixed; `leafDensity` (0.4..1) sets how many curtains hang.
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies the curtains.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface SaplingParams {
  readonly lean: number;        // −1..1, positive leans right
  readonly height: number;      // 0.7..1 of the frame
  readonly leafDensity: number; // 0.4..1
}
export type SaplingRecipe = PieceRecipe<'hush.sapling.v1', SaplingParams>;

export const SAPLING_FRAME = { w: 150, h: 240 } as const;

export const HUSH_SAPLING_WILLOW: SaplingRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.sapling.willow.v1', generatorId: 'hush.sapling.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { lean: 0.5, height: 0.95, leafDensity: 0.8 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi)) throw new Error(`hush.sapling.v1: ${k} must be ${lo}..${hi}`);
  return v;
}

const W = SAPLING_FRAME.w, H = SAPLING_FRAME.h;
const clamp = (p: Point): Point => ({ x: Math.min(W - 2, Math.max(2, p.x)), y: Math.min(H - 3, Math.max(2, p.y)) });

/** A tapering leaf curtain: top width `wt`, length `len`, bottom swayed by `sway`; edges notched like leaves. */
function curtain(x: number, y: number, wt: number, len: number, sway: number, jag: (i: number) => number): string {
  const K = Math.max(6, Math.round(len / 5));
  const left: Point[] = [], right: Point[] = [];
  for (let k = 0; k <= K; k++) {
    const t = k / K;
    const cx = x + sway * t * t;
    const half = (wt / 2) * Math.pow(1 - t, 1.15) * Math.min(1, 0.35 + t / 0.14) + 0.4; // rounded top, long taper
    const notch = k % 2 ? 1.6 : -0.2;
    left.push(clamp({ x: cx - half - notch * (1 - t) - jag(k), y: y + len * t }));
    right.unshift(clamp({ x: cx + half + notch * (1 - t) + jag(k + 50), y: y + len * t + (k % 2 ? 1.2 : 0) }));
  }
  return poly([...left, ...right]);
}

export function buildSapling(recipe: SaplingRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.sapling.v1');
  checkScope(scope);
  const lean = num(recipe.params, 'lean', -1, 1);
  const height = num(recipe.params, 'height', 0.7, 1);
  const density = num(recipe.params, 'leafDensity', 0.4, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // ---- trunk: a quadratic spine from the fixed base, leaning toward `lean` ----
  const base = { x: W / 2, y: H };
  const top = { x: W / 2 + lean * 34 * height, y: H - H * 0.7 * height };
  const ctrl = { x: W / 2 - lean * 6, y: H - H * 0.38 * height };
  const spine = (t: number): Point => ({
    x: (1 - t) ** 2 * base.x + 2 * (1 - t) * t * ctrl.x + t * t * top.x,
    y: (1 - t) ** 2 * (base.y - 0.8) + 2 * (1 - t) * t * ctrl.y + t * t * top.y,
  });
  const tl: Point[] = [], tr: Point[] = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12, p = spine(t), q = spine(Math.min(1, t + 0.01)), r0 = spine(Math.max(0, t - 0.01));
    const dx = q.x - r0.x, dy = q.y - r0.y, len = Math.hypot(dx, dy) || 1;
    const half = (i === 0 ? 7.5 : 5 - 3.3 * t) * (0.85 + 0.15 * height);
    const nx = -dy / len, ny = dx / len;
    tl.push({ x: p.x - nx * half, y: Math.min(H - 0.8, p.y - ny * half) });
    tr.unshift({ x: p.x + nx * half, y: Math.min(H - 0.8, p.y + ny * half) });
  }
  let trunk = el('path', { d: poly([...tl, ...tr]), fill: roles.woodBase, stroke: outline, 'stroke-width': 1.3, 'stroke-linejoin': 'round' });
  trunk += el('path', { d: poly(tl.slice(2, 11).map(p => ({ x: p.x + 1.6, y: p.y })), false), fill: 'none', stroke: roles.woodLight, 'stroke-width': 1.1, opacity: 0.8 });
  trunk += el('path', { d: poly(tr.slice(2, 10).map(p => ({ x: p.x - 1.5, y: p.y })), false), fill: 'none', stroke: roles.woodShadow, 'stroke-width': 1, opacity: 0.6 });

  // ---- branches: arch outward from the upper trunk, then droop ----
  let branches = '';
  const nb = 5;
  for (let i = 0; i < nb; i++) {
    const s = spine(0.55 + 0.4 * (i / (nb - 1)));
    const side = i % 2 ? -1 : 1;
    const reach = (22 + rnd(`branch.${i}.reach`) * 26) * height * (side === Math.sign(lean || 1) ? 1.15 : 0.8);
    const lift = (10 + rnd(`branch.${i}.lift`) * 14) * height;
    const end = clamp({ x: s.x + side * reach, y: s.y - lift * 0.3 + 6 });
    branches += el('path', {
      d: `M${n(s.x)} ${n(s.y)}Q${n(s.x + side * reach * 0.45)} ${n(s.y - lift)} ${n(end.x)} ${n(end.y)}`,
      fill: 'none', stroke: roles.woodShadow, 'stroke-width': 2.2 - i * 0.25, 'stroke-linecap': 'round',
    });
  }

  // ---- canopy: dome + weeping curtains in three values ----
  const canopy = { x: W / 2 + (top.x - W / 2) * 0.55, y: top.y - 12 * height };
  const rx = Math.min(60 * (0.8 + 0.2 * height), canopy.x - 14, W - 14 - canopy.x);
  const ry = 30 * height;
  const domeY = (x: number) => canopy.y - ry * Math.sqrt(Math.max(0, 1 - ((x - canopy.x) / rx) ** 2));
  const floor = H - 30 - (1 - height) * 30;
  const jagFor = (key: string) => (i: number) => (rnd(`${key}.j${i}`) - 0.5) * 1.4;
  const curtainAt = (key: string, u: number, lenScale: number, wt: number) => {
    const x = canopy.x + u * rx;
    const y = Math.min(domeY(x) + 4 + rnd(`${key}.y`) * 6, canopy.y + 2);
    const edge = Math.abs(u);
    const maxLen = floor - y;
    const len = Math.min(maxLen, (0.45 + 0.4 * edge + rnd(`${key}.len`) * 0.25) * maxLen * lenScale);
    const sway = lean * 4 + u * 5 + (rnd(`${key}.sway`) - 0.5) * 5;
    return curtain(x, y, wt, len, sway, jagFor(key));
  };

  const nBack = Math.round(10 + 10 * density);
  let back = '';
  for (let i = 0; i < nBack; i++) {
    const u = -1 + 2 * ((i + 0.5 + (rnd(`back.${i}.u`) - 0.5) * 0.6) / nBack);
    back += el('path', { d: curtainAt(`back.${i}`, u * 0.96, 1, 13 + rnd(`back.${i}.w`) * 7), fill: roles.reedShadow });
  }
  // One outlined silhouette pass under the curtains keeps the clean dark edge without a line on every curtain.
  const backGroup = el('g', { stroke: outline, 'stroke-width': 2.4, 'stroke-linejoin': 'round' }, back) + el('g', {}, back);

  let dome = '';
  const lobes = 9;
  const domePts: string[] = [];
  for (let i = 0; i <= lobes; i++) {
    const a = Math.PI + (Math.PI * i) / lobes;
    const p = { x: canopy.x + Math.cos(a) * rx * 0.98, y: canopy.y + Math.sin(a) * ry * 1.02 };
    const mid = Math.PI + (Math.PI * (i - 0.5)) / lobes;
    const c = { x: canopy.x + Math.cos(mid) * rx * 1.08, y: canopy.y + Math.sin(mid) * ry * 1.22 };
    domePts.push(i === 0 ? `M${n(p.x)} ${n(p.y)}` : `Q${n(c.x)} ${n(c.y)} ${n(p.x)} ${n(p.y)}`);
  }
  const domePath = `${domePts.join(' ')}Q${n(canopy.x)} ${n(canopy.y + ry * 0.55)} ${n(canopy.x - rx * 0.98)} ${n(canopy.y)}Z`;
  dome += el('path', { d: domePath, fill: roles.moss, stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' });

  const nMid = Math.round(7 + 9 * density);
  let mid = '';
  for (let i = 0; i < nMid; i++) {
    const u = -0.95 + 1.9 * ((i + rnd(`mid.${i}.u`)) / nMid);
    // Keep the lower trunk readable: curtains near the trunk stay short.
    const nearTrunk = Math.abs(canopy.x + u * rx - top.x) < 16 ? 0.55 : 0.85;
    const fill = rnd(`mid.${i}.c`) < 0.5 ? roles.reedBase : roles.moss;
    mid += el('path', { d: curtainAt(`mid.${i}`, u, nearTrunk, 9 + rnd(`mid.${i}.w`) * 6), fill, stroke: roles.reedShadow, 'stroke-width': 0.6 });
  }

  const nLight = Math.round(5 + 7 * density);
  let light = '';
  for (let i = 0; i < nLight; i++) {
    const u = -0.85 + 1.5 * ((i + rnd(`light.${i}.u`)) / nLight); // light comes from the upper left
    light += el('path', { d: curtainAt(`light.${i}`, u, 0.5, 2.5 + rnd(`light.${i}.w`) * 2), fill: roles.reedLight, opacity: 0.7 });
  }
  const domeLight = el('path', {
    d: `M${n(canopy.x - rx * 0.7)} ${n(canopy.y - ry * 0.35)}Q${n(canopy.x - rx * 0.2)} ${n(canopy.y - ry * 1.05)} ${n(canopy.x + rx * 0.35)} ${n(canopy.y - ry * 0.75)}`,
    fill: 'none', stroke: mix(roles.reedLight, roles.moss, 0.3), 'stroke-width': 5, 'stroke-linecap': 'round', opacity: 0.7,
  });

  // ---- ground: a small grass tuft at the root so the trunk sits in the ground ----
  let tuft = '';
  for (let i = 0; i < 7; i++) {
    const x = W / 2 - 12 + i * 4 + (rnd(`tuft.${i}`) - 0.5) * 2;
    const h = 6 + rnd(`tuft.${i}.h`) * 7;
    tuft += el('path', { d: `M${n(x - 1.6)} ${n(H - 0.6)}Q${n(x)} ${n(H - h * 0.6)} ${n(x + (i % 2 ? 2.5 : -2.5))} ${n(H - h)}Q${n(x + 0.6)} ${n(H - h * 0.4)} ${n(x + 1.6)} ${n(H - 0.6)}Z`,
      fill: i % 3 ? roles.reedBase : roles.reedShadow, stroke: outline, 'stroke-width': 0.6 });
  }

  const body = partGroup(parts, 'sapling-back-curtains', backGroup)
    + partGroup(parts, 'sapling-trunk', trunk + branches)
    + partGroup(parts, 'sapling-crown', dome + domeLight)
    + partGroup(parts, 'sapling-curtains', mid)
    + partGroup(parts, 'sapling-highlights', light)
    + partGroup(parts, 'sapling-ground', tuft);
  const support = { x: W / 2, y: H };
  return {
    frame: { ...SAPLING_FRAME },
    body,
    anchors: { support, trunk_base: support, canopy: { x: canopy.x, y: canopy.y } },
    parts,
  };
}
